import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'antd';
import { MenuOutlined, CloseOutlined, HeartFilled } from '@ant-design/icons';

const NAV_LINKS = [
  { label: 'Home',     to: '/'         },
  { label: 'Features', to: '/features' },
  { label: 'Pricing',  to: '/#pricing', scroll: true },
  { label: 'About',    to: '/about'    },
  { label: 'Contact',  to: '/contact'  },
];

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled]  = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location]);

  // Handle scroll to pricing section on page load
  useEffect(() => {
    if (location.hash === '#pricing') {
      setTimeout(() => {
        const pricingSection = document.getElementById('pricing');
        if (pricingSection) {
          pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location]);

  const handleLinkClick = (e, link) => {
    if (link.scroll) {
      e.preventDefault();
      setMenuOpen(false);
      
      if (location.pathname !== '/') {
        // Navigate to home page with hash
        navigate('/#pricing');
      } else {
        // Already on home page, just scroll
        const pricingSection = document.getElementById('pricing');
        if (pricingSection) {
          pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }
  };

  return (
    <>
      <style>{`
        .pub-nav {
          position: fixed; top: 0; left: 0; right: 0; z-index: 1000;
          transition: background .3s, box-shadow .3s;
          background: ${scrolled ? '#fff' : 'transparent'};
          box-shadow: ${scrolled ? '0 2px 16px rgba(0,21,41,0.10)' : 'none'};
        }
        .pub-nav-inner {
          max-width: 1200px; margin: 0 auto;
          display: flex; align-items: center; justify-content: space-between;
          padding: 0 32px; height: 68px;
        }
        .pub-logo {
          display: flex; align-items: center; gap: 10px;
          text-decoration: none;
        }
        .pub-logo-icon {
          width: 38px; height: 38px; border-radius: 10px;
          background: #001529;
          display: flex; align-items: center; justify-content: center;
        }
        .pub-logo-text {
          font-size: 18px; font-weight: 800;
          color: #001529; letter-spacing: -0.4px;
          font-family: 'Inter', sans-serif;
        }
        .pub-nav-links {
          display: flex; align-items: center; gap: 4px;
          list-style: none; margin: 0; padding: 0;
        }
        .pub-nav-links a {
          text-decoration: none;
          padding: 8px 16px; border-radius: 8px;
          font-size: 14px; font-weight: 500;
          color: #001529;
          transition: background .18s, color .18s;
        }
        .pub-nav-links a:hover   { background: #e6f4ff; color: #1890ff; }
        .pub-nav-links a.active  { background: #001529; color: #fff; }
        .pub-nav-right { display: flex; align-items: center; gap: 12px; }
        .pub-login-btn {
          height: 38px !important; padding: 0 22px !important;
          border-radius: 9px !important;
          background: #001529 !important; border-color: #001529 !important;
          font-weight: 600 !important; font-size: 13.5px !important;
        }
        .pub-login-btn:hover {
          background: #1890ff !important; border-color: #1890ff !important;
        }
        /* hamburger */
        .pub-hamburger {
          display: none; background: none; border: none;
          cursor: pointer; font-size: 22px; color: #001529;
          padding: 4px;
        }
        /* mobile menu */
        .pub-mobile-menu {
          display: none;
          position: fixed; top: 68px; left: 0; right: 0;
          background: #fff;
          box-shadow: 0 8px 24px rgba(0,0,0,.10);
          padding: 16px 24px 24px;
          z-index: 999;
          flex-direction: column; gap: 4px;
        }
        .pub-mobile-menu.open { display: flex; }
        .pub-mobile-menu a {
          text-decoration: none; padding: 12px 16px;
          border-radius: 8px; font-size: 15px; font-weight: 500;
          color: #001529; transition: background .15s;
        }
        .pub-mobile-menu a:hover  { background: #e6f4ff; color: #1890ff; }
        .pub-mobile-menu a.active { background: #001529; color: #fff; }

        @media (max-width: 768px) {
          .pub-nav-links, .pub-login-btn { display: none !important; }
          .pub-hamburger { display: block; }
        }
      `}</style>

      <nav className="pub-nav">
        <div className="pub-nav-inner">
          {/* Logo */}
          <Link to="/" className="pub-logo">
            <div className="pub-logo-icon">
              <HeartFilled style={{ color: '#1890ff', fontSize: 18 }} />
            </div>
            <span className="pub-logo-text">Wellness</span>
          </Link>

          {/* Desktop links */}
          <ul className="pub-nav-links">
            {NAV_LINKS.map(l => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className={location.pathname === l.to ? 'active' : ''}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>

          {/* CTA */}
          <div className="pub-nav-right">
            <Button type="primary" className="pub-login-btn">
              <Link to="/login" style={{ color: 'inherit', textDecoration: 'none' }}>
                Admin Login
              </Link>
            </Button>
            <button
              className="pub-hamburger"
              onClick={() => setMenuOpen(o => !o)}
              aria-label="Toggle menu"
            >
              {menuOpen ? <CloseOutlined /> : <MenuOutlined />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <div className={`pub-mobile-menu ${menuOpen ? 'open' : ''}`}>
        {NAV_LINKS.map(l => (
          <Link
            key={l.to}
            to={l.to}
            className={location.pathname === l.to ? 'active' : ''}
          >
            {l.label}
          </Link>
        ))}
        <Link to="/login" style={{ marginTop: 8 }}>
          <Button type="primary" style={{ width: '100%', borderRadius: 9, background: '#001529', borderColor: '#001529' }}>
            Admin Login
          </Button>
        </Link>
      </div>
    </>
  );
};

export default Navbar;
